/**
 * Recovery helpers for stale/missing code-split chunks.
 *
 * Every route component is a lazy `import()` chunk. When a long-lived tab runs
 * an older app version than what's deployed, navigation requests a hashed chunk
 * filename that no longer exists; the SPA host answers `200` with `index.html`
 * (text/html), so the dynamic import throws. These helpers detect that case and
 * recover by reloading onto the *current* asset graph.
 */

// Reload at most once per window. Persisted in sessionStorage so the throttle
// survives the reload itself — without that, a genuinely-broken deploy would
// reload-loop forever (the in-memory timestamp resets on every page load).
const RELOAD_THROTTLE_MS = 10_000;
const RELOAD_TIMESTAMP_KEY = "chunk-reload-ts";

/**
 * True when an error is a dynamic-import / code-split chunk load failure.
 *
 * Matched on the message because the error type varies by browser and bundler.
 * Covers Chrome/Edge + Vite (`Failed to fetch dynamically imported module`,
 * `Failed to load module script`), Vite's `error loading dynamically imported
 * module`, and Safari/Firefox (`Importing a module script failed`), plus rarer
 * browser variants that phrase the HTML-for-JS case as a module + MIME-type
 * error.
 */
export const isChunkLoadError = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("failed to fetch dynamically imported module") ||
    normalized.includes("error loading dynamically imported module") ||
    normalized.includes("importing a module script failed") ||
    normalized.includes("failed to load module script") ||
    // Require BOTH tokens: a bare "mime type" match would misclassify any
    // unrelated error that happens to mention MIME type (e.g. a route loader)
    // as a chunk failure — suppressing its Sentry report and forcing a reload.
    // The common Chrome case ("Failed to load module script… MIME type…") is
    // already matched above; this only adds rarer "module … MIME type" variants.
    (normalized.includes("mime type") && normalized.includes("module"))
  );
};

const readLastReload = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (private mode / quota) — treat as never reloaded.
    return 0;
  }
};

/**
 * True when a recovery reload happened within the throttle window. Read-only
 * (no reload, no write), so it's safe to call during render — e.g. to seed the
 * error-boundary's initial state and avoid flashing a transient "updating"
 * frame when recovery is already exhausted.
 */
export const isReloadThrottled = (): boolean =>
  Date.now() - readLastReload() < RELOAD_THROTTLE_MS;

/**
 * Persist the reload timestamp and confirm it actually stuck. Returns false when
 * storage is unavailable or silently drops the write (private-mode auto-wipe),
 * so the caller can refuse to reload rather than risk an unthrottled loop.
 */
const persistReloadTimestamp = (timestamp: number): boolean => {
  const value = String(timestamp);
  try {
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, value);
    return sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) === value;
  } catch {
    return false;
  }
};

/**
 * Recover from a stale chunk by reloading so the browser fetches the current
 * index.html (and current chunk hashes). Throttled across reloads to avoid a
 * loop when the asset is genuinely unavailable.
 *
 * Returns `true` if a reload was triggered (caller should show a transient
 * "updating" state). Returns `false` when throttled, **or** when the throttle
 * timestamp could not be durably recorded — in the latter case we deliberately
 * do not reload, because without a surviving timestamp the cross-reload guard
 * can't engage and reloading would risk an infinite loop. The caller surfaces
 * the real error instead.
 */
export const recoverFromChunkLoadError = (): boolean => {
  if (isReloadThrottled()) {
    return false;
  }
  if (!persistReloadTimestamp(Date.now())) {
    return false;
  }
  window.location.reload();
  return true;
};
